import { useCallback, useMemo, useState } from 'react';
import { Card, Avatar, Typography, Button, Tag, Tabs } from 'antd';
import { Link } from 'react-router-dom';
import { useAuthSession } from '@/entities/auth/api/hooks';
import { useEmployeeDomainData } from '@/entities/employee/api/hooks';
import { AddMedicalRecordModal } from '@/entities/medical/ui/AddMedicalRecordModal';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { EmployeeProfile } from '@/entities/employee/model/employeeApiSchema';
import type { Status } from '@/entities/user/model/userSchema';
import { stLabel } from '../constants';
import { MedicalEmployeeDetail } from '@/pages/Medical/EmployeeMedicalCard/EmployeeMedicalCard';
import { TrainingEmployeeDetail } from '@/pages/Training/EmployeeTrainingCard/EmployeeTrainingCard';
import { SizTab } from './SizTab';
import s from './EmployeeCard.module.scss';

const { Text, Title } = Typography;

const TAB_KEYS = {
  medical: 'medical',
  training: 'training',
  sout: 'sout',
  siz: 'siz',
} as const;

type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

function resolveDefaultTab(): TabKey {
  const params = new URLSearchParams(window.location.search);
  const tab = params.get('tab');
  if (tab && tab in TAB_KEYS) return tab as TabKey;
  return TAB_KEYS.medical;
}

function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return fullName.slice(0, 2).toUpperCase();
}

// DescRow — kept for SOUT tab reactivation
// function DescRow({ label, value }: { label: string; value: string }) { ... }

function overallDomainStatus(domain: {
  medical: { status: Status };
  riskAssessment: { status: Status };
  sout?: { status: Status };
}): Status {
  if (domain.medical.status === 'err' || domain.riskAssessment.status === 'err' || domain.sout?.status === 'err')
    return 'err';
  if (domain.medical.status === 'warn' || domain.riskAssessment.status === 'warn') return 'warn';
  return 'ok';
}

// WORK_ADMISSION_LABELS — kept for SOUT tab reactivation
// const WORK_ADMISSION_LABELS = { admitted: 'Допущен', not_admitted: 'Не допущен', revoked: 'Отозван' };

export function EmployeeCard({ profile, onBack }: { profile: EmployeeProfile; onBack: () => void }) {
  const { data: session } = useAuthSession();
  const isSelfEmployeeView = session?.user.role === 'employee' && session.user.employee_id === profile.id;

  const [activeTab, setActiveTab] = useState<string>(resolveDefaultTab);
  const [medicalModalOpen, setMedicalModalOpen] = useState(false);
  const { data: domain } = useEmployeeDomainData(profile.id);

  const medicalPrefill = useMemo(
    () => ({
      employeeId: profile.id,
      employeeName: profile.full_name,
      position: profile.position.name,
      department: profile.org_unit.name,
    }),
    [profile],
  );

  const openMedicalModal = useCallback(() => setMedicalModalOpen(true), []);
  const closeMedicalModal = useCallback(() => setMedicalModalOpen(false), []);

  const st = domain ? overallDomainStatus(domain) : 'ok';

  const tabItems = useMemo(
    () => [
      {
        key: TAB_KEYS.medical,
        label: 'Медосмотры',
        children: (
          <div className={s['root__tab-content']}>
            <MedicalEmployeeDetail
              employeeId={profile.id}
              showBack={false}
              hideHero
              embeddedInEmployeeCard
              onOpenAddMedicalRecord={openMedicalModal}
            />
          </div>
        ),
      },
      {
        key: TAB_KEYS.training,
        label: 'Обучение',
        children: (
          <div className={s['root__tab-content']}>
            <TrainingEmployeeDetail employeeId={profile.id} showBack={false} hideProfile embeddedInEmployeeCard />
          </div>
        ),
      },
      {
        key: TAB_KEYS.siz,
        label: 'СИЗ',
        children: <SizTab employeeId={profile.id} />,
      },
      // {
      //   key: TAB_KEYS.sout,
      //   label: 'СОУТ и ОПР',
      //   children: (
      //     <div className={s['root__tab-content']}>
      //       <Card size="small" className={s['root__section-card']}>
      //         <Text strong className={s['root__section-label']}>
      //           СОУТ и оценка профессиональных рисков
      //         </Text>
      //         <Spin spinning={domainLoading}>
      //           {domain ? (
      //             <>
      //               <DescRow label="Рабочее место" value={domain.workplaceId} />
      //               <DescRow label="Класс УТ" value={domain.sout.class} />
      //               <DescRow label="Дата СОУТ" value={domain.sout.date} />
      //               <DescRow label="Пересмотр до" value={domain.sout.nextDate} />
      //               <Divider style={{ margin: '12px 0' }} />
      //               <Link to={`/sout?employeeId=${profile.id}`}>Раздел СОУТ и ОПР →</Link>
      //             </>
      //           ) : (
      //             <Empty description="Нет данных" />
      //           )}
      //         </Spin>
      //       </Card>
      //     </div>
      //   ),
      // },
    ],
    [profile, openMedicalModal],
  );

  const displayedTabItems = tabItems;
  const tabsActiveKey = activeTab in TAB_KEYS ? activeTab : TAB_KEYS.medical;

  return (
    <div>
      {isSelfEmployeeView ? (
        <Link to="/" className={s['root__back-btn']}>
          ← На главную
        </Link>
      ) : (
        <Button type="link" className={s['root__back-btn']} onClick={onBack}>
          ← К списку сотрудников
        </Button>
      )}

      <Card className={s['root__hero-card']} classNames={{ body: s['root__hero-card-body-zero'] }}>
        <div className={s['root__hero-row']}>
          <Avatar size={56} className={s['root__hero-avatar']}>
            {initials(profile.full_name)}
          </Avatar>
          <div className={s['root__hero-text']}>
            <Title level={4} className={s['root__hero-name']}>
              {profile.full_name}
            </Title>
            <Text className={s['root__hero-position']}>{profile.position.name}</Text>
            <Text className={s['root__hero-dept']}>{profile.org_unit.name}</Text>
            <div className={s['root__hero-tags']}>
              <Tag className={s['root__tag-dark']}>Таб. {profile.tab_number ?? '—'}</Tag>
              <StatusBadge status={st} label={stLabel[st]} />
              {profile.is_active ? <Tag color="success">Активен</Tag> : null}
            </div>
          </div>
        </div>
      </Card>

      <Tabs activeKey={tabsActiveKey} onChange={setActiveTab} items={displayedTabItems} className={s['root__tabs']} />

      <AddMedicalRecordModal open={medicalModalOpen} onClose={closeMedicalModal} prefill={medicalPrefill} />
    </div>
  );
}
